import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import ImageThumb from "@/components/ImageThumb";
import CardBadges from "@/components/CardBadges";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, ShieldCheck, Package, Home, MapPin, Truck, Loader2, Store } from "lucide-react";

export default function Cart() {
  const [items, setItems] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [shipping, setShipping] = useState("locker");
  const [pickupPoint, setPickupPoint] = useState("");
  const [addr, setAddr] = useState({ name: "", street1: "", street2: "", city: "", state: "", zip: "", country: "US", phone: "" });
  const [quote, setQuote] = useState(null);
  const [liveGroups, setLiveGroups] = useState([]);   // [{seller_id, seller_name, count, rates:[...]}]
  const [liveSel, setLiveSel] = useState({});          // {seller_id: rate}
  const [ratesLoading, setRatesLoading] = useState(false);
  const [carrierFilter, setCarrierFilter] = useState("all");
  const [stripeLive, setStripeLive] = useState(true);
  const nav = useNavigate();

  const load = () => api.get("/cart").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/config").then(r => setStripeLive(!!r.data.stripe_live)).catch(() => {}); }, []);
  useEffect(() => {
    const key = shipping === "live" ? "locker" : shipping;
    api.get("/cart/quote", { params: { shipping: key } }).then(r => setQuote(r.data)).catch(() => {});
  }, [shipping, items]);

  useEffect(() => {
    if (liveGroups.length || Object.keys(liveSel).length) {
      setLiveGroups([]); setLiveSel({});
      setShipping((s) => (s === "live" ? "locker" : s));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addr.street1, addr.city, addr.state, addr.zip]);

  const del = async (id) => { await api.delete(`/cart/${id}`); load(); };

  // ---- Group the bag by seller (basis of one combined shipping charge per seller) ----
  const sellerGroups = React.useMemo(() => {
    const m = {};
    items.forEach((i) => {
      const sid = i.listing.seller_id || "_";
      const g = m[sid] || (m[sid] = { seller_id: sid, seller_name: i.listing.seller_name || "Seller", items: [], subtotal: 0 });
      g.items.push(i);
      g.subtotal += Number(i.offer_price ?? i.listing.price) * i.qty;
    });
    return Object.values(m);
  }, [items]);
  const multiSeller = sellerGroups.length > 1;

  const subtotal = quote?.subtotal ?? items.reduce((s, i) => s + Number(i.offer_price ?? i.listing.price) * i.qty, 0);
  const protection = quote?.protection?.amount ?? 0;

  const selectedOpt = (quote?.options || []).find((o) => o.key === shipping);
  const isPickupPoint = selectedOpt?.kind === "pickup_point";
  const needsAddress = shipping === "live" || (selectedOpt && selectedOpt.kind !== "local");
  const addrReady = addr.street1 && addr.city && addr.zip;

  const fetchRates = async () => {
    if (!addrReady) { toast.error("Enter your street, city and ZIP first"); return; }
    setRatesLoading(true);
    try {
      const { data } = await api.post("/shipping/rates", { ship_to: addr });
      const groups = data.groups || [];
      setLiveGroups(groups);
      const sel = {};
      groups.forEach((g) => { if (g.rates?.length) sel[g.seller_id] = g.rates[0]; });
      setLiveSel(sel);
      setCarrierFilter("all");
      if (groups.some((g) => g.rates?.length)) setShipping("live");
      else toast("No live rates for this address — flat options are still available");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setRatesLoading(false); }
  };

  const liveReady = shipping === "live" && liveGroups.length > 0 && liveGroups.every((g) => liveSel[g.seller_id]);
  const liveShipTotal = liveGroups.reduce((s, g) => s + (liveSel[g.seller_id]?.amount || 0), 0);
  const flatShipTotal = quote?.shipping_total ?? quote?.shipping?.price ?? 0;
  const shipTotal = shipping === "live" && liveReady ? liveShipTotal : flatShipTotal;
  const shipLabel = shipping === "live"
    ? `Shipping${multiSeller ? ` · ${sellerGroups.length} sellers` : ""}`
    : (multiSeller ? `${quote?.shipping?.label || "Shipping"}` : (quote?.shipping?.label || "Shipping"));
  const grandTotal = Number(subtotal) + Number(protection) + Number(shipTotal);

  const etaDays = (r) => { const m = /(\d+)/.exec(r.eta || ""); return m ? parseInt(m[1], 10) : 99; };
  const arriveBy = (r) => {
    const n = etaDays(r);
    if (n >= 99) return null;
    const d = new Date();
    let added = 0;
    while (added < n) { d.setDate(d.getDate() + 1); if (d.getDay() !== 0 && d.getDay() !== 6) added += 1; }
    return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  };

  const checkout = async () => {
    if (isPickupPoint && !pickupPoint) { toast.error("Choose a pickup point first"); return; }
    if (needsAddress && !(addr.name && addr.street1 && addr.city && addr.zip)) {
      toast.error("Please enter your delivery address (name, street, city, ZIP)"); return;
    }
    if (shipping === "live" && !liveReady) { toast.error("Pick a shipping rate for each seller"); return; }
    setProcessing(true);
    try {
      const payload = {
        origin_url: window.location.origin, shipping,
        pickup_point_id: isPickupPoint ? pickupPoint : null,
        ship_to: needsAddress ? addr : null,
      };
      if (shipping === "live" && liveReady) {
        payload.live_selections = liveGroups.map((g) => ({
          seller_id: g.seller_id, service: liveSel[g.seller_id].service, provider: liveSel[g.seller_id].provider,
        }));
      }
      const { data } = await api.post("/payments/checkout", payload);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Checkout failed");
      setProcessing(false);
    }
  };

  const KIND_ICON = { pickup_point: Package, home: Home, local: MapPin };

  const renderRateRow = (g, r, idx) => {
    const rates = g.rates || [];
    const cheapest = rates.length ? Math.min(...rates.map((x) => x.amount)) : null;
    const fastest = rates.length ? Math.min(...rates.map(etaDays)) : 99;
    const fastestCheapest = rates.length ? Math.min(...rates.filter((x) => etaDays(x) === fastest).map((x) => x.amount)) : null;
    const on = liveSel[g.seller_id]?.service === r.service && liveSel[g.seller_id]?.provider === r.provider;
    const isCheapest = r.amount === cheapest;
    const isFastest = etaDays(r) === fastest && r.amount === fastestCheapest && fastest < 99;
    return (
      <button key={idx} onClick={() => { setShipping("live"); setLiveSel((v) => ({ ...v, [g.seller_id]: r })); }}
        data-testid={`live-rate-${g.seller_id}-${idx}`}
        className="w-full hairline p-3 flex items-center gap-3 text-left transition-colors"
        style={on ? { borderColor: "#1E3A2B", background: "rgba(30,58,43,0.05)" } : {}}>
        <span className="shrink-0 w-4 h-4 rounded-full border flex items-center justify-center"
          style={{ borderColor: on ? "#1E3A2B" : "#B7B2A8" }}>
          {on && <span className="w-2 h-2 rounded-full" style={{ background: "#1E3A2B" }} />}
        </span>
        <span className="flex-1 min-w-0">
          <span className="text-sm flex items-center gap-1.5 flex-wrap">
            {r.service}
            {isCheapest && (
              <span className="text-[0.55rem] uppercase tracking-[0.12em] px-1 py-0.5" style={{ background: "#1E3A2B", color: "#F9F6F0" }}>Cheapest</span>
            )}
            {isFastest && !isCheapest && (
              <span className="text-[0.55rem] uppercase tracking-[0.12em] px-1 py-0.5" style={{ background: "#2563EB", color: "#fff" }}>Fastest</span>
            )}
          </span>
          <span className="text-xs text-neutral-500 block truncate">{r.provider}{r.eta ? ` · ${r.eta}` : ""}</span>
          {arriveBy(r) && (
            <span className="text-[0.62rem] block" style={{ color: "#1E3A2B" }} data-testid={`arrive-${g.seller_id}-${idx}`}>
              Arrives by {arriveBy(r)}
            </span>
          )}
        </span>
        <span className="font-display shrink-0">${r.amount.toFixed(2)}</span>
      </button>
    );
  };

  return (
    <div data-testid="cart-page" className="max-w-[1200px] mx-auto px-6 lg:px-12 py-10">
      <div className="hairline-b pb-6">
        <div className="label-eyebrow">Your Bag</div>
        <h1 className="font-display text-5xl tracking-tighter mt-2">Checkout.</h1>
      </div>
      {items.length === 0 ? (
        <div className="hairline p-16 text-center mt-10">
          <div className="label-eyebrow">Your bag is empty</div>
          <Link to="/browse" className="btn-primary inline-flex mt-6">Continue shopping</Link>
        </div>
      ) : (
        <div className="mt-10 grid lg:grid-cols-[1fr_360px] gap-10">
          <div>
            {multiSeller && (
              <p className="mb-4 text-sm text-neutral-600 flex items-center gap-2" data-testid="bundle-note">
                <Store size={14} style={{ color: "#1E3A2B" }} />
                Your bag has items from {sellerGroups.length} sellers — you'll be charged one combined shipping fee per seller.
              </p>
            )}
            {sellerGroups.map((g) => (
              <div key={g.seller_id} className="mb-6" data-testid={`cart-group-${g.seller_id}`}>
                {multiSeller && (
                  <div className="flex items-center justify-between hairline-b pb-2 mb-1">
                    <Link to={`/seller/${g.seller_id}`} className="text-sm font-medium flex items-center gap-1.5 hover:underline">
                      <Store size={13} className="text-neutral-500" /> {g.seller_name}
                    </Link>
                    <span className="text-xs text-neutral-500">{g.items.length} item{g.items.length > 1 ? "s" : ""} · one label</span>
                  </div>
                )}
                <ul className="divide-y divide-black/10">
                  {g.items.map((i) => (
                    <li key={i.id} className="py-5 flex items-center gap-4" data-testid={`cart-row-${i.id}`}>
                      <Link to={`/product/${i.listing.id}`}>
                        <div className="w-24 h-24 hairline overflow-hidden">
                          <ImageThumb path={i.listing.image_paths?.[0]} className="w-full h-full object-cover" />
                        </div>
                      </Link>
                      <div className="flex-1 min-w-0">
                        <Link to={`/product/${i.listing.id}`} className="font-display truncate block">{i.listing.title}</Link>
                        <div className="text-xs text-neutral-500 mt-1">Qty {i.qty}</div>
                        {i.offer_price != null && (
                          <div className="mt-1 text-[0.65rem] uppercase tracking-wider font-medium" style={{ color: "#2563EB" }} data-testid={`cart-offer-tag-${i.id}`}>
                            Offer applied
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        {i.offer_price != null && (
                          <div className="text-xs text-neutral-400 line-through">${(Number(i.listing.price) * i.qty).toFixed(2)}</div>
                        )}
                        <div className="font-display">${(Number(i.offer_price ?? i.listing.price) * i.qty).toFixed(2)}</div>
                      </div>
                      <button className="flex flex-col items-center p-2 hover:bg-black/5" onClick={() => del(i.id)} data-testid={`remove-cart-${i.id}`} title="Remove from bag">
                        <Trash2 size={16} />
                        <span className="text-[9px] tracking-[0.06em] mt-0.5 uppercase">Remove</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <aside className="hairline p-6 h-fit">
            <div className="label-eyebrow">Delivery</div>
            <p className="text-xs text-neutral-500 mt-1">
              {multiSeller ? "One combined shipping charge per seller — buy more from one closet and save." : "Choose how you'd like it sent — just like your favourite resale apps."}
            </p>
            <div className="mt-3 space-y-2" data-testid="shipping-options">
              {(() => {
                const opts = quote?.options || [];
                const paid = opts.filter((o) => o.price > 0).map((o) => o.price);
                const cheapest = paid.length ? Math.min(...paid) : null;
                return opts.map((o) => {
                  const on = shipping === o.key;
                  const Icon = KIND_ICON[o.kind] || Package;
                  const perSeller = o.price > 0 && multiSeller;
                  return (
                    <button key={o.key} onClick={() => setShipping(o.key)} data-testid={`shipping-${o.key}`}
                      className="w-full hairline p-3 flex items-center gap-3 text-left transition-colors"
                      style={on ? { borderColor: "#1E3A2B", background: "rgba(30,58,43,0.05)" } : {}}>
                      <span className="shrink-0 w-4 h-4 rounded-full border flex items-center justify-center"
                        style={{ borderColor: on ? "#1E3A2B" : "#B7B2A8" }}>
                        {on && <span className="w-2 h-2 rounded-full" style={{ background: "#1E3A2B" }} />}
                      </span>
                      <Icon size={16} strokeWidth={1.6} className="shrink-0 text-neutral-500" />
                      <span className="flex-1 min-w-0">
                        <span className="text-sm flex items-center gap-2">
                          {o.label}
                          {o.price === cheapest && o.price > 0 && (
                            <span className="text-[0.58rem] uppercase tracking-[0.14em] px-1.5 py-0.5" style={{ background: "#1E3A2B", color: "#F9F6F0" }}>Cheapest</span>
                          )}
                        </span>
                        <span className="text-xs text-neutral-500 block truncate">
                          {o.carrier} · {o.eta}{perSeller ? ` · ×${sellerGroups.length} sellers` : ""}
                        </span>
                      </span>
                      <span className="font-display shrink-0">
                        {o.price ? `$${(perSeller ? o.price * sellerGroups.length : o.price).toFixed(2)}` : "Free"}
                      </span>
                    </button>
                  );
                });
              })()}
            </div>

            {isPickupPoint && (
              <div className="mt-3" data-testid="pickup-point-picker">
                <div className="label-eyebrow mb-2">Choose a pickup point</div>
                <select className="w-full hairline p-3 text-sm bg-white" data-testid="pickup-point-select"
                  value={pickupPoint} onChange={(e) => setPickupPoint(e.target.value)}>
                  <option value="">Select a nearby point…</option>
                  {(quote?.pickup_points || []).map((pp) => (
                    <option key={pp.id} value={pp.id}>{pp.name} — {pp.address}</option>
                  ))}
                </select>
              </div>
            )}

            {needsAddress && (
              <div className="mt-4" data-testid="ship-to-form">
                <div className="label-eyebrow mb-2">Delivery address</div>
                <div className="grid grid-cols-2 gap-2">
                  <input className="hairline p-2.5 text-sm col-span-2 bg-white outline-none focus:border-black" placeholder="Full name"
                    data-testid="ship-name" value={addr.name} onChange={(e) => setAddr((v) => ({ ...v, name: e.target.value }))} />
                  <input className="hairline p-2.5 text-sm col-span-2 bg-white outline-none focus:border-black" placeholder="Street address"
                    data-testid="ship-street1" value={addr.street1} onChange={(e) => setAddr((v) => ({ ...v, street1: e.target.value }))} />
                  <input className="hairline p-2.5 text-sm col-span-2 bg-white outline-none focus:border-black" placeholder="Apt, suite (optional)"
                    data-testid="ship-street2" value={addr.street2} onChange={(e) => setAddr((v) => ({ ...v, street2: e.target.value }))} />
                  <input className="hairline p-2.5 text-sm bg-white outline-none focus:border-black" placeholder="City"
                    data-testid="ship-city" value={addr.city} onChange={(e) => setAddr((v) => ({ ...v, city: e.target.value }))} />
                  <div className="flex gap-2">
                    <input className="hairline p-2.5 text-sm w-20 bg-white outline-none focus:border-black" placeholder="State"
                      data-testid="ship-state" value={addr.state} onChange={(e) => setAddr((v) => ({ ...v, state: e.target.value }))} />
                    <input className="hairline p-2.5 text-sm flex-1 min-w-0 bg-white outline-none focus:border-black" placeholder="ZIP"
                      data-testid="ship-zip" value={addr.zip} onChange={(e) => setAddr((v) => ({ ...v, zip: e.target.value }))} />
                  </div>
                  <input className="hairline p-2.5 text-sm col-span-2 bg-white outline-none focus:border-black" placeholder="Phone (for the carrier)"
                    data-testid="ship-phone" value={addr.phone} onChange={(e) => setAddr((v) => ({ ...v, phone: e.target.value }))} />
                </div>
              </div>
            )}

            {needsAddress && (
              <div className="mt-3" data-testid="live-rates">
                <button type="button" onClick={fetchRates} disabled={ratesLoading || !addrReady}
                  className="btn-outline w-full flex items-center justify-center gap-2 text-sm disabled:opacity-50"
                  data-testid="get-live-rates-btn">
                  {ratesLoading ? <Loader2 size={14} className="animate-spin" /> : <Truck size={14} />}
                  {ratesLoading ? "Fetching live shipping rates…" : "Get live shipping rates"}
                </button>
                {liveGroups.map((g) => {
                  const carriers = [...new Set((g.rates || []).map((r) => r.provider))];
                  if (!g.rates?.length) return (
                    <p key={g.seller_id} className="mt-3 text-xs text-neutral-500">No live rates for {g.seller_name} — pick a flat option above.</p>
                  );
                  return (
                    <div key={g.seller_id} className="mt-3 space-y-2" data-testid={`live-rates-group-${g.seller_id}`}>
                      <div className="label-eyebrow mb-1 flex items-center gap-1.5">
                        {multiSeller ? <><Store size={11} /> {g.seller_name}</> : "Live shipping rates"}
                      </div>
                      {carriers.length > 1 && (
                        <div className="flex flex-wrap gap-1.5 mb-1" data-testid={`carrier-filter-${g.seller_id}`}>
                          {["all", ...carriers].map((c) => (
                            <button key={c} onClick={() => setCarrierFilter(c)} data-testid={`carrier-filter-${g.seller_id}-${c}`}
                              className="text-[0.62rem] uppercase tracking-[0.12em] px-2 py-1 hairline transition-colors"
                              style={carrierFilter === c ? { borderColor: "#1E3A2B", background: "#1E3A2B", color: "#F9F6F0" } : {}}>
                              {c === "all" ? "All carriers" : c}
                            </button>
                          ))}
                        </div>
                      )}
                      {g.rates.map((r, idx) => {
                        if (carrierFilter !== "all" && r.provider !== carrierFilter) return null;
                        return renderRateRow(g, r, idx);
                      })}
                    </div>
                  );
                })}
                {liveGroups.length > 0 && (
                  <p className="mt-2 text-[0.62rem] text-neutral-500">Prefer a flat rate? Pick any option above — they stay available.</p>
                )}
              </div>
            )}

            <div className="label-eyebrow mt-7">Summary</div>
            <div className="mt-3 space-y-2 text-sm">
              <div className="flex items-baseline justify-between">
                <span>Subtotal</span>
                <span data-testid="cart-total">${Number(subtotal).toFixed(2)}</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span className="flex items-center gap-1.5">
                  Buyer protection
                  <span className="text-xs text-neutral-500">
                    {quote ? `${(quote.protection.rate * 100).toFixed(0)}% + $${quote.protection.fixed.toFixed(2)}` : ""}
                  </span>
                </span>
                <span data-testid="cart-protection-fee">${Number(protection).toFixed(2)}</span>
              </div>
              <div className="flex items-baseline justify-between">
                <span>{shipLabel}</span>
                <span data-testid="cart-shipping-fee">
                  {shipTotal ? `$${Number(shipTotal).toFixed(2)}` : "Free"}
                </span>
              </div>
              {multiSeller && shipTotal > 0 && (
                <p className="text-[0.62rem] text-neutral-500 -mt-1" data-testid="cart-shipping-breakdown">
                  One combined charge for each of your {sellerGroups.length} sellers.
                </p>
              )}
              <div className="flex items-baseline justify-between pt-3 mt-1 border-t border-black/10">
                <span className="label-eyebrow">Total</span>
                <span className="font-display text-2xl" data-testid="cart-grand-total">
                  ${Number(grandTotal).toFixed(2)}
                </span>
              </div>
            </div>
            <p className="mt-4 flex items-start gap-2 text-[0.68rem] text-neutral-600 leading-relaxed">
              <ShieldCheck size={13} className="mt-0.5 shrink-0" style={{ color: "#2563EB" }} />
              <span>
                Buyer protection covers the 3-day authenticity guarantee: we hold the seller's payout until you
                confirm the item is genuine.{" "}
                <Link to="/pricing" className="underline whitespace-nowrap" data-testid="cart-fees-link">See all fees</Link>
              </span>
            </p>
            <button disabled={processing} onClick={checkout} className="btn-primary w-full mt-5" data-testid="checkout-btn">
              {processing ? "Redirecting…" : "Checkout with Stripe"}
            </button>
            <CardBadges className="mt-3 justify-center" />
            <p className="mt-3 text-[0.65rem] text-neutral-500 leading-relaxed">
              {stripeLive
                ? "Payments are securely processed by Stripe. We never see or store your card details."
                : "Test card: 4242 4242 4242 4242 · any future expiry · any CVC."}
            </p>
          </aside>
        </div>
      )}
    </div>
  );
}
